import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { MetricCategory } from '@chiroup/core/constants/metricCategories';

export type EventLogClinic = {
  clinicId: number;
  clinicName: string;
  status: string;
  plan: string;
  totalEvents: number;
  activeUsers: number;
  appointments: number;
  encounters: number;
  payments: number;
};

export type EventLogEvent = {
  id: number;
  clinicId: number;
  userId: string | null;
  locationId: number | null;
  eventType: string;
  metadata: Record<string, unknown> | null;
  occurredAt: number;
  userName: string | null;
};

export type EventLogUser = {
  userId: string;
  userName: string;
  userEmail: string;
  role: string;
  metrics: Record<string, number>;
  daily: number[];
  totalActivity: number;
};

export type EventLogClinicSummary = {
  clinic: {
    ID: number;
    name: string;
    status: string;
    plan: string;
    subscriptionStartDate?: string | null;
  } | null;
  metrics: Record<string, number>;
  previousMetrics: Record<string, number>;
  categoryTotals: Record<MetricCategory, number>;
  kpis: {
    totalEvents: number;
    activeUsers: number;
    timeInAppMinutes: number;
    daysActive: number;
    rangeDays: number;
  };
  previousKpis: {
    totalEvents: number;
    activeUsers: number;
    timeInAppMinutes: number;
    daysActive: number;
  };
  periodStart: string;
  periodEnd: string;
  previousPeriodStart: string;
  previousPeriodEnd: string;
};

export type Granularity = 'daily' | 'weekly' | 'monthly';

export type EventLogClinicDaily = {
  dates: string[];
  daily: { date: string; total: number }[];
  dailyByMetric: Record<string, number[]>;
  granularity: Granularity;
};

const eventLogService = () => {
  const listClinics = async (params: {
    search?: string;
    skip?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{ data: EventLogClinic[]; skip?: number; count: number }> => {
    return ChiroUpAPI.get('api', '/event-logs/clinics', { queryParams: params });
  };

  const listEvents = async (
    clinicId: number,
    params: {
      skip?: number;
      limit?: number;
      eventType?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<{ data: EventLogEvent[]; skip?: number; count: number }> => {
    return ChiroUpAPI.get('api', `/event-logs/clinics/${clinicId}/events`, {
      queryParams: params,
    });
  };

  const listUsers = async (
    clinicId: number,
    params: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<{ data: EventLogUser[]; dates: string[] }> => {
    return ChiroUpAPI.get('api', `/event-logs/clinics/${clinicId}/users`, {
      queryParams: params,
    });
  };

  const getClinicSummary = async (
    clinicId: number,
    params: {
      startDate?: string;
      endDate?: string;
      userId?: string;
    }
  ): Promise<EventLogClinicSummary> => {
    return ChiroUpAPI.get('api', `/event-logs/clinics/${clinicId}/summary`, {
      queryParams: params,
    });
  };

  const getClinicDaily = async (
    clinicId: number,
    params: {
      startDate?: string;
      endDate?: string;
      userId?: string;
    }
  ): Promise<EventLogClinicDaily> => {
    return ChiroUpAPI.get('api', `/event-logs/clinics/${clinicId}/daily`, {
      queryParams: params,
    });
  };

  return {
    listClinics,
    listEvents,
    listUsers,
    getClinicSummary,
    getClinicDaily,
  };
};

export default eventLogService();
